/**
 * complianceReviewsApi — Compliance reviews (Bill 2026-11-04): the Innovation Hub change tracker's items flagged for a
 * compliance review, read and decided from the Risk panel.
 *
 *   GET  /v3/risk/compliance-reviews/summary              who reviews, whether you do, counts per view
 *   GET  /v3/risk/compliance-reviews/items?view=…          needs-review | open | reviewed | all
 *   GET  /v3/risk/compliance-reviews/items/{key}           one item: fields, history, links
 *   POST /v3/risk/compliance-reviews/items/{key}/review    { action, note, expectedVersion }
 *
 * pcb-retail-api passes each call to the Hub on behalf of the person signed in here (taken from the bearer, never
 * from anything this page sends). The Hub decides who may review. Bearer-only; no function key in the bundle.
 */
import { buildHeaders, getBaseUrl } from './riskSettingsApi';

export type ReviewView = 'needs-review' | 'open' | 'reviewed' | 'all';
export type ReviewAction = 'confirm' | 'clear' | 'note' | 'request-revert';

export interface ReviewViewer {
  readonly upn: string | null;
  readonly name: string | null;
  readonly isReviewer: boolean;
  readonly isDeveloper: boolean;
  readonly canReview: boolean;
}

export interface ReviewPerson {
  readonly upn: string;
  readonly name: string;
}

export interface ReviewSummary {
  readonly platform: string;
  readonly viewer: ReviewViewer;
  readonly reviewers: ReadonlyArray<ReviewPerson>;
  readonly counts: { readonly needsReview: number; readonly open: number; readonly reviewed: number; readonly all: number };
}

/** One row of the queue (the Hub's review row). */
export interface ReviewRow {
  readonly key: string;
  readonly source: string;
  readonly platform: string;
  readonly sourceRef: string;
  readonly requestedBy: string;
  readonly requestedByName: string;
  readonly requestedAt: string;
  readonly category: string | null;
  readonly oneLiner: string | null;
  readonly approvalStatus: string;
  readonly approver: string | null;
  readonly approverName: string | null;
  readonly approvedAt: string | null;
  readonly deployedAt: string | null;
  readonly dispositionStatus: string;
  readonly status: string;
  readonly determination: string | null;
  readonly complianceFlag: string;
  readonly revertRequested: boolean;
  readonly updatedAt: string | null;
  readonly complianceRationale?: string | null;
  readonly complianceNote?: string | null;
  readonly complianceReviewedBy?: string | null;
  readonly complianceReviewedAt?: string | null;
  readonly filedAt?: string | null;
}

export interface ReviewLink {
  readonly linkId: number;
  readonly type: string;
  readonly ref: string;
  readonly label: string | null;
  readonly addedBy: string;
  readonly addedAt: string;
}

export interface ReviewEvent {
  readonly eventId: number;
  readonly eventType: string;
  readonly actor: string;
  readonly reason: string | null;
  readonly detail: Record<string, unknown> | null;
  readonly occurredAt: string;
}

/** One item's full record (the Hub's detail). */
export interface ReviewDetail extends ReviewRow {
  readonly requestedChange: string | null;
  readonly determinationNote: string | null;
  readonly complianceRationale: string | null;
  readonly complianceNote: string | null;
  readonly complianceReviewedBy: string | null;
  readonly complianceReviewedAt: string | null;
  readonly revertRequestedBy: string | null;
  readonly revertRequestedAt: string | null;
  readonly revertReason: string | null;
  readonly revertAskId: number | null;
  readonly filedAt: string | null;
  readonly triagedAt: string | null;
  readonly decidedAt: string | null;
  readonly builtAt: string | null;
  readonly verifiedAt: string | null;
  readonly revertedAt: string | null;
  readonly closedAt: string | null;
  readonly intake: Record<string, unknown> | null;
  readonly version: string;
  readonly links: ReadonlyArray<ReviewLink>;
  readonly events: ReadonlyArray<ReviewEvent>;
}

export interface RevertAskOutcome {
  readonly filed: boolean;
  readonly askId?: number;
  readonly error?: string;
}

/** A failed call: the words to show, the server's code and status (409 = the item changed meanwhile). */
export class ReviewApiError extends Error {
  constructor(message: string, readonly code: string | null, readonly status: number) {
    super(message);
    this.name = 'ReviewApiError';
  }
}

interface Envelope { success?: boolean; error?: string; code?: string }

async function read<T extends Envelope>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: T | null = null;
  try { body = text ? (JSON.parse(text) as T) : null; } catch { body = null; }
  if (!resp.ok || (body && body.success === false)) {
    const fallback = resp.status === 403
      ? 'Compliance reviews are for the Risk Management team (RiskManager role).'
      : `The server answered ${resp.status}.`;
    throw new ReviewApiError(body?.error ?? fallback, body?.code ?? null, resp.status);
  }
  if (!body) throw new ReviewApiError('Empty response from the server.', null, resp.status);
  return body;
}

const base = (): string => `${getBaseUrl()}/v3/risk/compliance-reviews`;

export async function loadReviewSummary(): Promise<ReviewSummary> {
  const resp = await fetch(`${base()}/summary`, { method: 'GET', headers: await buildHeaders() });
  return read<Envelope & ReviewSummary>(resp);
}

export async function loadReviewItems(view: ReviewView): Promise<{ total: number; items: ReadonlyArray<ReviewRow>; viewer: ReviewViewer }> {
  const resp = await fetch(`${base()}/items?view=${encodeURIComponent(view)}`, { method: 'GET', headers: await buildHeaders() });
  const body = await read<Envelope & { total?: number; items?: ReviewRow[]; viewer: ReviewViewer }>(resp);
  return { total: body.total ?? body.items?.length ?? 0, items: body.items ?? [], viewer: body.viewer };
}

export async function loadReviewItem(key: string): Promise<{ item: ReviewDetail; viewer: ReviewViewer }> {
  const resp = await fetch(`${base()}/items/${encodeURIComponent(key)}`, { method: 'GET', headers: await buildHeaders() });
  return read<Envelope & { item: ReviewDetail; viewer: ReviewViewer }>(resp);
}

export async function submitReview(
  key: string,
  action: ReviewAction,
  note: string,
  expectedVersion: string,
): Promise<{ item: ReviewDetail; revertAsk: RevertAskOutcome | null }> {
  const resp = await fetch(`${base()}/items/${encodeURIComponent(key)}/review`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify({ action, note, expectedVersion }),
  });
  return read<Envelope & { item: ReviewDetail; revertAsk: RevertAskOutcome | null }>(resp);
}
